import React, { useState, useEffect } from 'react';
import './App.css';
import { getApiUrl } from './config';

// Version 0.1.1 - Force cache refresh - 2025-09-06T20:51:30.289Z
console.log('Cache refresh timestamp:', Date.now());

function App() {
  const [phones, setPhones] = useState([]);
  const [filteredPhones, setFilteredPhones] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [telegramUser, setTelegramUser] = useState(null);
  const [showOffer, setShowOffer] = useState(false);
  const [detailPhone, setDetailPhone] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  // Cart state
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedMemory, setSelectedMemory] = useState([]);
  const [selectedSIM, setSelectedSIM] = useState([]);

  // Collapsible filter sections
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    search: false,
    price: false,
    features: false,
    colors: false,
    memory: false,
    sim: false
  });

  // Filter options for each category - dynamically generated from categories
  const filterOptions = categories.reduce((acc, category) => {
    acc[category.slug] = {
      features: category.slug === 'phone' ? ['A19 Pro', '48MP камера', 'USB-C', 'Dual nanoSim', 'eSim', 'nanoSim+eSim', 'Без Rustore', '120Hz дисплей'] : [],
      colors: category.slug === 'phone' ? ['Синий', 'Белый', 'Оранжевый', 'Фиолетовый', 'Голубой', 'Зеленый', 'Золотой'] :
              category.slug === 'watch' ? ['Серый', 'Чёрный', 'Золотой', 'Розовый', 'Серебро', 'Синий', 'Зелёный'] : [],
      memory: category.slug === 'phone' ? ['256 ГБ', '512 ГБ', '1 ТБ', '2 ТБ'] :
               category.slug === 'watch' ? ['64GB'] : [],
      sim: category.slug === 'phone' ? ['Dual nanoSim', 'nanoSim+eSim', 'eSim'] : []
    };
    return acc;
  }, {}) || {};

  // Function to send logs to server
  const sendLog = async (message, data = {}) => {
    try {
      await fetch(getApiUrl('/api/log'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          data,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to send log:', error);
    }
  };

  // Cart functions
  const addToCart = (phone) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === phone.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === phone.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { ...phone, quantity: 1 }];
      }
    });
    sendLog('Item added to cart', { phoneId: phone.id, phoneName: phone.name });
  };

  const removeFromCart = (phoneId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== phoneId));
    sendLog('Item removed from cart', { phoneId });
  };

  const updateCartItemQuantity = (phoneId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(phoneId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === phoneId ? { ...item, quantity } : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const clearCart = () => {
    setCart([]);
    sendLog('Cart cleared');
  };

  // Filter function
  const applyFilters = () => {
    let filtered = phones;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(phone => phone.category === selectedCategory);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(phone =>
        phone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        phone.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Price range filter
    if (priceRange.min) {
      filtered = filtered.filter(phone => phone.fullPrice >= parseInt(priceRange.min));
    }
    if (priceRange.max) {
      filtered = filtered.filter(phone => phone.fullPrice <= parseInt(priceRange.max));
    }

    // Features filter
    if (selectedFeatures.length > 0) {
      filtered = filtered.filter(phone =>
        selectedFeatures.every(feature =>
          phone.features && phone.features.some(phoneFeature =>
            phoneFeature.toLowerCase().includes(feature.toLowerCase())
          )
        )
      );
    }

    // Colors filter
    if (selectedColors.length > 0) {
      filtered = filtered.filter(phone =>
        selectedColors.some(color =>
          phone.name.toLowerCase().includes(color.toLowerCase()) ||
          (phone.specs && phone.specs['Цвет'] && phone.specs['Цвет'].toLowerCase().includes(color.toLowerCase()))
        )
      );
    }

    // Memory filter
    if (selectedMemory.length > 0) {
      filtered = filtered.filter(phone =>
        selectedMemory.some(memory =>
          phone.name.toLowerCase().includes(memory.toLowerCase()) ||
          (phone.specs && phone.specs['Память'] && phone.specs['Память'].toLowerCase().includes(memory.toLowerCase()))
        )
      );
    }

    // SIM filter
    if (selectedSIM.length > 0) {
      filtered = filtered.filter(phone =>
        selectedSIM.some(sim =>
          phone.name.toLowerCase().includes(sim.toLowerCase()) ||
          (phone.specs && phone.specs['SIM'] && phone.specs['SIM'].toLowerCase().includes(sim.toLowerCase()))
        )
      );
    }

    setFilteredPhones(filtered);
  };

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [phones, selectedCategory, searchTerm, priceRange, selectedFeatures, selectedColors, selectedMemory, selectedSIM]);

  // Reset filters function
  const resetFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    setPriceRange({ min: '', max: '' });
    setSelectedFeatures([]);
    setSelectedColors([]);
    setSelectedMemory([]);
    setSelectedSIM([]);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    console.log('🚀 APP START: Initializing application...');
    sendLog('App initialized', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    console.log('🔄 NEW VERSION LOADED - Cache refresh enabled');
    sendLog('NEW VERSION LOADED', { version: 'cache-refresh-enabled', timestamp: new Date().toISOString() });

    // Step 1: Determine execution context
    console.log('📍 STEP 1: Determining execution context...');
    const isTelegramWebApp = !!(window.Telegram && window.Telegram.WebApp);
    const hasTgWebAppData = window.location.hash.includes('tgWebAppData');
    const isInBrowser = !isTelegramWebApp && !hasTgWebAppData;

    console.log('Context analysis:', {
      isTelegramWebApp,
      hasTgWebAppData,
      isInBrowser,
      userAgent: navigator.userAgent.substring(0, 50) + '...'
    });

    sendLog('Execution context determined', {
      isTelegramWebApp,
      hasTgWebAppData,
      isInBrowser,
      userAgent: navigator.userAgent
    });

    // Step 2: Try to get user data from Telegram WebApp API
    console.log('📍 STEP 2: Attempting to get user data from WebApp API...');
    let userData = null;

    if (isTelegramWebApp) {
      console.log('✅ Telegram WebApp detected, trying WebApp API...');
      try {
        const webApp = window.Telegram.WebApp;
        userData = webApp.initDataUnsafe?.user;

        if (userData) {
          console.log('✅ User data obtained from WebApp API:', userData);
          setTelegramUser(userData);
          sendLog('User data from WebApp API', {
            userId: userData.id,
            username: userData.username,
            source: 'webapp_api'
          });
        } else {
          console.log('❌ WebApp API returned no user data');
          sendLog('No user data from WebApp API');
        }
      } catch (error) {
        console.error('❌ Error accessing WebApp API:', error);
        sendLog('Error accessing WebApp API', { error: error.message });
      }
    } else {
      console.log('❌ Telegram WebApp not available');
    }

    // Step 3: Fallback to URL parsing if no user data from WebApp
    if (!userData && hasTgWebAppData) {
      console.log('📍 STEP 3: No user data from WebApp, trying URL parsing...');
      sendLog('Attempting URL parsing fallback');

      try {
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const tgWebAppData = params.get('tgWebAppData');

        if (tgWebAppData) {
          console.log('✅ Found tgWebAppData in URL');
          const decodedData = decodeURIComponent(tgWebAppData);
          const dataParams = new URLSearchParams(decodedData);
          const userJson = dataParams.get('user');

          if (userJson) {
            userData = JSON.parse(decodeURIComponent(userJson));
            console.log('✅ User data parsed from URL:', userData);
            setTelegramUser(userData);
            sendLog('User data parsed from URL', {
              userId: userData.id,
              username: userData.username,
              source: 'url_parsing'
            });
          } else {
            console.log('❌ No user JSON in URL data');
            sendLog('No user JSON in URL data');
          }
        } else {
          console.log('❌ No tgWebAppData in URL');
          sendLog('No tgWebAppData in URL');
        }
      } catch (error) {
        console.error('❌ Error parsing URL data:', error);
        sendLog('Error parsing URL data', { error: error.message });
      }
    }

    // Step 4: Use test data only as last resort
    if (!userData) {
      console.log('📍 STEP 4: No real user data available, using test data...');
      if (isInBrowser) {
        console.log('✅ Using browser test data');
        userData = {
          id: 123456789,
          username: 'browser_test',
          first_name: 'Браузерный',
          last_name: 'Тест',
          photo_url: null
        };
      } else {
        console.log('✅ Using Telegram test data');
        userData = {
          id: 987654321,
          username: 'telegram_test',
          first_name: 'Telegram',
          last_name: 'Test',
          photo_url: null
        };
      }

      setTelegramUser(userData);
      sendLog('Using test user data', {
        userId: userData.id,
        username: userData.username,
        reason: isInBrowser ? 'browser_fallback' : 'telegram_fallback'
      });
    }

    console.log('🎯 FINAL: User data set to:', userData);
    sendLog('User data initialization complete', {
      finalUserId: userData.id,
      finalUsername: userData.username,
      dataSource: userData.username === 'browser_test' ? 'browser_test' :
                  userData.username === 'telegram_test' ? 'telegram_test' :
                  'real_data'
    });


    // Load phones from API
    fetch(getApiUrl('/api/phones'))
      .then(res => res.json())
      .then(data => {
        setPhones(data);
        setFilteredPhones(data); // Initialize filtered phones
        sendLog('Phones loaded successfully', { count: data.length });
      })
      .catch(error => {
        console.error('Error loading phones:', error);
        sendLog('Error loading phones', { error: error.message });
      })
      .finally(() => {
        // Ensure filteredPhones is set even if there's an error
        if (phones.length > 0 && filteredPhones.length === 0) {
          setFilteredPhones(phones);
        }
      });

    // Load categories from API
    fetch(getApiUrl('/api/categories'))
      .then(res => res.json())
      .then(data => {
        setCategories(data);
        sendLog('Categories loaded successfully', { count: data.length });
      })
      .catch(error => {
        console.error('Error loading categories:', error);
        sendLog('Error loading categories', { error: error.message });
      });

    // Завершаем загрузку пользователя
    setUserLoading(false);
   }, []);

   console.log('🎨 Rendering App component with telegramUser:', telegramUser);
   console.log('🎨 telegramUser state:', { telegramUser, isNull: telegramUser === null, isUndefined: telegramUser === undefined });

  return (
    <div className="App">
      {/* User Profile в правом верхнем углу */}
      {telegramUser && <UserProfile user={telegramUser} />}

      <header className="hero">
        <h1>EKStoreApp</h1>
        <p className="hero-subtitle">Магазин премиальной техники по лучшим ценам в СНГ</p>
        <div className="hero-features">
          <div className="feature">
            <span className="feature-icon">🚀</span>
            <span>Быстрая доставка</span>
          </div>
          <div className="feature">
            <span className="feature-icon">💎</span>
            <span>Премиум качество</span>
          </div>
          <div className="feature">
            <span className="feature-icon">🔒</span>
            <span>Гарантия Apple</span>
          </div>
        </div>
        <button onClick={() => setShowFiltersModal(true)} className="filters-toggle-btn">
          <span className="filters-icon">⚡</span>
          <span className="filters-text">Фильтры</span>
          {(selectedCategory !== 'all' || searchTerm || priceRange.min || priceRange.max ||
            selectedFeatures.length > 0 || selectedColors.length > 0 ||
            selectedMemory.length > 0 || selectedSIM.length > 0) && (
            <span className="filters-count">
              {[
                selectedCategory !== 'all' ? 1 : 0,
                searchTerm ? 1 : 0,
                priceRange.min || priceRange.max ? 1 : 0,
                selectedFeatures.length,
                selectedColors.length,
                selectedMemory.length,
                selectedSIM.length
              ].reduce((a, b) => a + b, 0)}
            </span>
          )}
        </button>

        {/* Cart Button */}
        <button onClick={() => setShowCart(true)} className="cart-toggle-btn">
          <span className="cart-icon">🛒</span>
          <span className="cart-text">Корзина</span>
          {getCartItemCount() > 0 && (
            <span className="cart-count">{getCartItemCount()}</span>
          )}
        </button>
      </header>

      {/* Filters Modal */}
      {showFiltersModal && (
        <FiltersModal
          show={showFiltersModal}
          onClose={() => setShowFiltersModal(false)}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={setSelectedFeatures}
          selectedColors={selectedColors}
          setSelectedColors={setSelectedColors}
          selectedMemory={selectedMemory}
          setSelectedMemory={setSelectedMemory}
          selectedSIM={selectedSIM}
          setSelectedSIM={setSelectedSIM}
          filterOptions={filterOptions}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          resetFilters={resetFilters}
          filteredPhones={filteredPhones}
          phones={phones}
          categories={categories}
        />
      )}

      <div className="phones">
        {filteredPhones && filteredPhones.map(phone => (
          <div key={phone.id} className={`phone-card ${phone.preorder ? 'preorder' : ''}`}>
            {phone.preorder && <div className="preorder-badge">ПРЕДЗАКАЗ</div>}
            {phone.images && phone.images[0] && <img src={phone.images[0]} alt={phone.name} className="phone-preview" />}
            <h3>{phone.name}</h3>
            <p className="phone-description">{phone.description}</p>
            <div className="phone-features">
              {phone.features && phone.features.map((feature, index) => (
                <span key={index} className="feature-tag">{feature}</span>
              ))}
            </div>
            <div className="price">
              {phone.fullPrice ? `${phone.fullPrice.toLocaleString()} ₽` : `от ${phone.price} ₽`}
              {phone.fullPrice && <div className="booking-price">Бронь: {phone.price} ₽</div>}
              {phone.preorder && phone.fullPrice && <div className="preorder-price">Полная цена: {phone.fullPrice} ₽</div>}
            </div>
            <button onClick={() => {
              setDetailPhone(phone);
              sendLog('Phone details opened', { phoneId: phone.id, phoneName: phone.name });
            }} className="select-btn">
              Подробнее
            </button>
            <button onClick={() => addToCart(phone)} className="add-to-cart-btn">
              Добавить в корзину
            </button>
          </div>
        ))}
      </div>

      {/* Company Information Section */}
      <div className="company-section">
        <div className="company-info">
          <h2>EKStoreApp</h2>
          <p className="company-description">
            Магазин премиальной техники по лучшим ценам в СНГ.
          </p>

          <div className="company-features">
            <div className="feature-item">
              <span className="feature-icon">🚚</span>
              <span>Самовывоз Москва/Питер</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📦</span>
              <span>Доставка по РФ</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🎉</span>
              <span>Проводим регулярные конкурсы</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⭐</span>
              <span>Премиальная техника</span>
            </div>
          </div>
        </div>


        <div className="contacts-section">
          <h3>Контакты</h3>
          <div className="contact-info">
            <div className="contact-item">
              <span className="contact-icon">📱</span>
              <span>+7 952 226-85-19</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">🏢</span>
              <span>г. Санкт-Петербург. Спасский переулок 14/35, БЦ на Сенной, офис 213</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📋</span>
              <span>ИНН: 780632045038</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">🚚</span>
              <span>Самовывоз: Москва/Питер</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📦</span>
              <span>Доставка по РФ</span>
            </div>
          </div>
        </div>

        <div className="developer-info">
          <h3>О магазине</h3>
          <p>
            <strong>EKStoreApp</strong> - ваш надежный партнер в мире премиальной техники.
            Мы предлагаем лучшие цены и гарантию качества на всю продукцию.
          </p>
        </div>
      </div>

      {selectedPhone && (
        <div className="selected">
          <div className="selected-info">
            <h2>{selectedPhone.name}</h2>
            <p>{selectedPhone.description}</p>
            <div className="selected-price">{selectedPhone.price} ₽</div>
          </div>
          <button onClick={() => {
            setShowForm(true);
            sendLog('Booking form opened', {
              phoneId: selectedPhone.id,
              phoneName: selectedPhone.name,
              preorder: selectedPhone.preorder
            });
          }} className="book-btn">
            {selectedPhone.preorder ? 'Предзаказать' : 'Забронировать'}
          </button>
        </div>
      )}

      {showForm && (
        <BookingForm
          phone={selectedPhone}
          cart={cart}
          telegramUser={telegramUser}
          onClose={() => setShowForm(false)}
          onShowOffer={() => setShowOffer(true)}
          sendLog={sendLog}
          isFullPrice={selectedPhone?.isFullPrice || false}
        />
      )}

      {showOffer && <OfferModal onClose={() => setShowOffer(false)} />}

      {detailPhone && (
        <PhoneDetail
          phone={detailPhone}
          onClose={() => setDetailPhone(null)}
          onBook={(isFullPrice = false) => {
            setSelectedPhone({...detailPhone, isFullPrice});
            setDetailPhone(null);
            setShowForm(true);
          }}
          onAddToCart={() => {
            addToCart(detailPhone);
            setDetailPhone(null);
          }}
        />
      )}

      {/* Cart Modal */}
      {showCart && (
        <CartModal
          cart={cart}
          onClose={() => setShowCart(false)}
          onRemoveItem={removeFromCart}
          onUpdateQuantity={updateCartItemQuantity}
          onClearCart={clearCart}
          onCheckout={(paymentType) => {
            setShowCart(false);
            setSelectedPhone({ paymentType }); // Pass payment type to booking form
            setShowForm(true);
          }}
          getCartTotal={getCartTotal}
          sendLog={sendLog}
        />
      )}
    </div>
  );
}

function PhoneDetail({ phone, onClose, onBook, onAddToCart }) {
  const handlePurchase = (isFullPrice = false) => {
    // Pass purchase type to booking function
    onBook(isFullPrice);
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    if (phone.images && phone.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % phone.images.length);
    }
  };

  const prevImage = () => {
    if (phone.images && phone.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + phone.images.length) % phone.images.length);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content phone-detail" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="phone-detail-header">
          <div className="image-gallery">
            {phone.images && phone.images[currentImageIndex] && <img src={phone.images[currentImageIndex]} alt={phone.name} className="phone-image" />}
            {phone.images && phone.images.length > 1 && (
              <>
                <button className="gallery-nav prev" onClick={prevImage}>‹</button>
                <button className="gallery-nav next" onClick={nextImage}>›</button>
                <div className="gallery-dots">
                  {phone.images && phone.images.map((_, index) => (
                    <span
                      key={index}
                      className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="phone-info">
            <h2>{phone.name}</h2>
            {phone.preorder && <div className="preorder-badge">ПРЕДЗАКАЗ</div>}
            <div className="price">
              {phone.fullPrice ? `${phone.fullPrice.toLocaleString()} ₽` : `${phone.price} ₽`}
              {phone.fullPrice && <div className="booking-price">Бронь: {phone.price} ₽</div>}
              {phone.preorder && <div className="preorder-price">Полная цена: {phone.fullPrice} ₽</div>}
            </div>
          </div>
        </div>

        <div className="phone-description-full">
          <h3>Описание</h3>
          <p>{phone.fullDescription || 'Описание не доступно'}</p>
        </div>

        <div className="phone-features">
          <h3>Особенности</h3>
          <div className="features-grid">
            {phone.features && phone.features.length > 0 ? (
              phone.features.map((feature, index) => (
                <span key={index} className="feature-tag">{feature}</span>
              ))
            ) : (
              <p>Особенности не доступны</p>
            )}
          </div>
        </div>

        <div className="phone-specs">
           <h3>Характеристики</h3>
           <div className="specs-list">
             {phone.specs && Object.keys(phone.specs).length > 0 ? (
               Object.entries(phone.specs).map(([key, value]) => (
                 <div key={key} className="spec-item">
                   <span className="spec-name">{key}:</span>
                   <span className="spec-value">{value}</span>
                 </div>
               ))
             ) : (
               <p>Характеристики не доступны</p>
             )}
           </div>
         </div>

         <div className="phone-warranties">
           <h3>Гарантии</h3>
           <div className="warranties-list">
             {phone.shopWarranty && (
               <div className="warranty-item shop-warranty">
                 <span className="warranty-icon">🏪</span>
                 <span className="warranty-text">Гарантия магазина</span>
               </div>
             )}
             {phone.officialWarranty && (
               <div className="warranty-item official-warranty">
                 <span className="warranty-icon">✅</span>
                 <span className="warranty-text">Официальная гарантия</span>
               </div>
             )}
             {!phone.shopWarranty && !phone.officialWarranty && (
               <p>Информация о гарантиях не доступна</p>
             )}
           </div>
         </div>

        <div className="phone-detail-actions">
           {phone.preorder ? (
             <>
               <button onClick={() => handlePurchase(false)} className="book-btn preorder-btn">
                 Предзаказать {phone.price} ₽
               </button>
               {phone.fullPrice && (
                 <button onClick={() => handlePurchase(true)} className="book-btn full-price-btn">
                   Купить полностью {phone.fullPrice} ₽
                 </button>
               )}
             </>
           ) : (
             <button onClick={() => handlePurchase(false)} className="book-btn">
               Забронировать {phone.price} ₽
             </button>
           )}
           <button onClick={onAddToCart} className="add-to-cart-btn">
             Добавить в корзину
           </button>
         </div>
      </div>
    </div>
  );
}

function BookingForm({ phone, cart, telegramUser, onClose, onShowOffer, sendLog, isFullPrice = false }) {
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [acceptOffer, setAcceptOffer] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Determine payment type from phone prop (for cart bookings)
  const paymentType = phone?.paymentType || (isFullPrice ? 'full' : 'preorder');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptOffer) {
      alert('Пожалуйста, примите условия оферты');
      return;
    }

    setIsProcessing(true);

    // Determine if we're booking from cart or single item
    const isCartBooking = cart && cart.length > 0;
    const itemsToBook = isCartBooking ? cart : [phone];

    // Calculate total based on payment type
    let totalAmount;
    if (isCartBooking) {
      totalAmount = cart.reduce((total, item) => {
        const itemPrice = paymentType === 'full' && item.fullPrice ? item.fullPrice : item.price;
        return total + (itemPrice * item.quantity);
      }, 0);
    } else {
      totalAmount = paymentType === 'full' && phone.fullPrice ? phone.fullPrice : phone.price;
    }

    const bookingData = {
      items: itemsToBook.map(item => ({
        phoneId: item.id,
        name: item.name,
        price: paymentType === 'full' && item.fullPrice ? item.fullPrice : item.price,
        quantity: item.quantity || 1,
        isFullPrice: paymentType === 'full'
      })),
      customerInfo: formData,
      telegramUser: telegramUser?.id,
      telegramUsername: telegramUser?.username,
      isCartBooking: isCartBooking,
      purchaseType: paymentType
    };

    try {
      sendLog('Payment creation started', {
        itemsCount: itemsToBook.length,
        totalAmount: totalAmount,
        isCartBooking: isCartBooking,
        userId: telegramUser?.id
      });

      // Create payment with booking data
      const paymentRes = await fetch(getApiUrl('/api/payment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totalAmount,
          description: isCartBooking
            ? `Бронирование ${itemsToBook.length} товаров`
            : `Бронирование ${phone.name}`,
          bookingData: bookingData
        })
      });

      if (paymentRes.ok) {
        const { paymentUrl, paymentId } = await paymentRes.json();
        console.log('Payment created:', paymentId);
        sendLog('Payment created successfully', { paymentId, paymentUrl });

        // paymentId will be passed through return_url from Yookassa

        // Redirect to Yookassa payment page
        // After payment, Yookassa will redirect to payment-status page
        window.location.href = paymentUrl;
      } else {
        const errorData = await paymentRes.json();
        sendLog('Payment creation failed', { error: errorData.error });
        alert('Ошибка при создании платежа: ' + (errorData.error || 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('Payment error:', error);
      sendLog('Payment error', { error: error.message });
      alert('Произошла ошибка при обработке платежа: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        <h2>
          {cart && cart.length > 0
            ? `Бронирование ${cart.length} товаров`
            : `Бронирование ${phone.name}`
          }
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              placeholder="ФИО"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="tel"
              placeholder="Номер телефона"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              required
            />
          </div>

          <div className="offer-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={acceptOffer}
                onChange={(e) => setAcceptOffer(e.target.checked)}
                required
              />
              <span>Я принимаю условия </span>
              <button type="button" className="offer-link" onClick={onShowOffer}>
                публичной оферты
              </button>
            </label>
          </div>

          <button type="submit" className="submit-btn" disabled={!acceptOffer || isProcessing}>
            {isProcessing ? 'Обработка...' : `Оплатить ${
              cart && cart.length > 0
                ? cart.reduce((total, item) => {
                    const itemPrice = paymentType === 'full' && item.fullPrice ? item.fullPrice : item.price;
                    return total + (itemPrice * item.quantity);
                  }, 0)
                : (paymentType === 'full' && phone.fullPrice ? phone.fullPrice : phone.price)
            } ₽`}
          </button>
        </form>
      </div>
    </div>
  );
}

function OfferModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content offer-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        <h2>Публичная оферта</h2>
        <div className="offer-content">
          <p><strong>ПУБЛИЧНАЯ ОФЕРТА</strong></p>
          <p>Настоящий Договор является публичным предложением заключить договор купли-продажи товаров дистанционным способом с любым лицом, принявшим условия настоящего Договора путем акцепта (совершения действий по приобретению товара).</p>

          <h3>1. Предмет договора</h3>
          <p>Продавец обязуется передать покупателю товар, забронированный по предоплате, в мини приложение https://t.me/EKStoreApp_botа. Покупатель принять этот товар и оплатить его.</p>

          <h3>2. Стоимость товара и порядок формирования цены</h3>
          <p>2.1. Цена товара устанавливается продавцом самостоятельно и публикуется на сайте продавца. Все цены являются ориентировочными и могут быть изменены Продавцом в одностороннем порядке в любое время до момента оплаты Покупателем полной стоимости товара.</p>
          <p>2.2. Окончательная цена Товара, подлежащая оплате Покупателем, формируется на дату фактического поступления Товара на склад Продавца и указывается в счете на оплату, высылаемом Покупателю после поступления Товара.</p>
          <p>Покупатель обязан оплатить полную стоимость товара в течение 3 дней с момента получения счета на оплату.</p>
          <p>2.3. В случае если после подтверждения Заказа и до фактической поставки товара Покупателю рыночная цена изменится, Продавец обязуется:</p>
          <ul>
            <li>уведомить Покупателя о новой цене в срок не позднее 48 часов с момента изменения;</li>
            <li>предоставить Покупателю право в течение 72 часов от момента уведомления либо согласиться на новую цену и сохранить заказ, либо отказаться от заказа с полным возвратом Предоплаты за минусом расходов, прямо предусмотренных настоящим договором.</li>
          </ul>
          <p>2.4. Если Покупатель отказывается от товара вследствие изменения цены, Продавец возвращает Предоплату в сроки, предусмотренные законодательством, за вычетом документально подтвержденных расходов на логистику и комиссии (включая удержания посредников и перевозчиков), если такие расходы возникли до момента отказа. Размер удержаний и их основания указываются в уведомлении о возврате.</p>

          <h3>3. Условия доставки и возврата</h3>
          <p>3.1. Доставка осуществляется на условиях и сроках, указанных при оформлении заказа. Возможны задержки в связи с особенностями поставки и действиями третьих лиц (поставщики, перевозчики, таможня).</p>
          <p>3.2. В случае возврата товара по инициативе Покупателя после отправки заказа применяются следующие положения:</p>
          <ul>
            <li>В случае полного отказа Покупателя от Заказа до момента отгрузки Товара в службу доставки, внесенная Предоплата подлежит полному возврату Покупателю в течение 10 (десяти) календарных дней с момента получения отказа.</li>
            <li>В случае отказа Покупателя от Заказа после момента его отгрузки в службу доставки, возврат денежных средств осуществляется за вычетом всех фактических расходов на логистику, согласно п. 2.4. настоящей оферты. При этом внесенная Предоплата также учитывается в общей сумме к возврату.</li>
          </ul>
          <p>3.3. Продавец информирует о возможных удержаниях заранее; при наличии условий у перевозчика (например, СДЭК) о праве удерживать процент от стоимости в случае возврата, Покупатель признает и соглашается с таким условием, а сумма удержания может быть удержана из суммы к возврату.</p>
          <p>3.4. Конкретные проценты и суммы удержаний и их основания указываются в уведомлении о возврате.</p>

          <h3>4. Порядок оплаты</h3>
          <p>4.1. Оплата заказа осуществляется способами, предложенными Продавцом при оформлении заказа.</p>
          <p>4.2. В случае изменения цены после оплаты и до отправки товара, порядок возврата/доплаты регулируется пунктом 2.3 настоящего договора.</p>

          <h3>5. Акцепт оферты и заключение договора</h3>
          <p>5.1. Договор считается заключенным с момента акцепта оферты Покупателем (Предоплата в размере 10000 рублей), за исключением случаев, когда Продавец в разумный срок уведомляет о невозможности выполнения заказа (отсутствие товара, форс‑мажор и т.п.).</p>
          <p>Размер Предоплаты будет автоматически засчитан в счет оплаты окончательной стоимости Товара.</p>
          <p>Покупатель обязан доплатить разницу между окончательной ценой Товара и внесенной Предоплатой в порядке и сроки, указанные в п. 2.2 настоящей оферты.</p>
          <p>Внесение Предоплаты означает полное и безоговорочное согласие Покупателя со всеми условиями настоящей оферты, включая особые условия предзаказа и условия о возможном изменении окончательной цены Товара.</p>
          <p>Предоплата является гарантией серьезности намерений Покупателя и резервируется Продавцом для обеспечения бронирования Товара за Покупателем.</p>
          <p>5.2. Продавец оставляет за собой право отменить заказ в случае достоверного выявления мошенничества, ошибок в цене или недостоверных данных Покупателя с уведомлением и полным возвратом средств за минусом фактических расходов, если такие имели место.</p>

          <h3>6. Ответственность сторон</h3>
          <p>6.1. Стороны несут ответственность в соответствии с действующим законодательством и условиями настоящего договора.</p>
          <p>6.2. Продавец не несет ответственности за убытки Покупателя, вызванные изменением рыночной цены товара после акцепта оферты, если Продавец надлежаще уведомил Покупателя и предоставил возможность отказаться от заказа с возвратом средств согласно п.3.2.</p>

          <h3>7. Форс‑мажор</h3>
          <p>7.1. События непреодолимой силы освобождают стороны от ответственности за неисполнение обязательств на срок действия таких обстоятельств.</p>

          <h3>8. Разрешение споров</h3>
          <p>8.1. Споры решаются путем переговоров, при недостижении согласия — в суде в порядке, установленном действующим законодательством.</p>

          <h3>9. Прочие условия</h3>
          <p>9.1. Полный текст условий доставки, удержаний и комиссий публикуется на сайте Продавца и является составной частью оферты.</p>
          <p>9.2. Настоящая оферта вступает в силу с момента публикации на официальном сайте продавца и действует бессрочно до отзыва её продавцом. Изменение условий настоящей оферты производится продавцом единолично без дополнительного уведомления покупателей. Изменения, внесенные Продавцом в Договор и опубликованные на сайте в форме актуализированной Оферты, считаются принятыми Покупателем в полном объеме.</p>
          <p>Подписывая настоящий договор или совершая покупку, покупатель подтверждает свое согласие с вышеуказанными условиями.</p>
          <p>Отзыв оферты не влияет на уже акцептованные заказы, за исключением случаев, прямо предусмотренных в оферте.</p>
        </div>
      </div>
    </div>
  );
}

function CartModal({ cart, onClose, onRemoveItem, onUpdateQuantity, onClearCart, onCheckout, getCartTotal, sendLog }) {
  const [paymentType, setPaymentType] = useState('preorder'); // 'preorder' or 'full'

  if (cart.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content cart-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose}>×</button>
          <h2>Корзина</h2>
          <div className="empty-cart">
            <p>Ваша корзина пуста</p>
            <button onClick={onClose} className="continue-shopping-btn">
              Продолжить покупки
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getTotalByType = (type) => {
    if (type === 'full') {
      return cart.reduce((total, item) => total + ((item.fullPrice || item.price) * item.quantity), 0);
    }
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const hasPreorderItems = cart.some(item => item.preorder);
  const hasFullPriceItems = cart.some(item => item.fullPrice && item.fullPrice !== item.price);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cart-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        <h2>Корзина</h2>

        <div className="cart-items">
          {cart.map(item => (
            <div key={item.id} className="cart-item">
              <div className="cart-item-info">
                <h3>{item.name}</h3>
                <div className="cart-item-prices">
                  <p className="cart-item-price">
                    Предзаказ: {item.price.toLocaleString()} ₽
                    {item.fullPrice && item.fullPrice !== item.price && (
                      <span className="full-price">Полная цена: {item.fullPrice.toLocaleString()} ₽</span>
                    )}
                  </p>
                  {item.preorder && <span className="preorder-badge">ПРЕДЗАКАЗ</span>}
                </div>
              </div>
              <div className="cart-item-controls">
                <div className="quantity-controls">
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                    className="quantity-btn"
                  >
                    -
                  </button>
                  <span className="quantity">{item.quantity}</span>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    className="quantity-btn"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className="remove-item-btn"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Type Selection */}
        {(hasPreorderItems || hasFullPriceItems) && (
          <div className="payment-type-section">
            <h3>Выберите тип оплаты:</h3>
            <div className="payment-type-options">
              <label className="payment-type-option">
                <input
                  type="radio"
                  value="preorder"
                  checked={paymentType === 'preorder'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span className="payment-type-label">
                  <strong>Предзаказ</strong>
                  <span className="payment-type-desc">Оплата части стоимости сейчас</span>
                </span>
              </label>
              {hasFullPriceItems && (
                <label className="payment-type-option">
                  <input
                    type="radio"
                    value="full"
                    checked={paymentType === 'full'}
                    onChange={(e) => setPaymentType(e.target.value)}
                  />
                  <span className="payment-type-label">
                    <strong>Полная стоимость</strong>
                    <span className="payment-type-desc">Оплата полной цены товара</span>
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        <div className="cart-summary">
          <div className="cart-total">
            <strong>
              Итого ({paymentType === 'full' ? 'полная стоимость' : 'предзаказ'}):
              {getTotalByType(paymentType).toLocaleString()} ₽
            </strong>
          </div>
          <div className="cart-actions">
            <button onClick={onClearCart} className="clear-cart-btn">
              Очистить корзину
            </button>
            <button
              onClick={() => onCheckout(paymentType)}
              className="checkout-btn"
            >
              Оформить заказ ({paymentType === 'full' ? 'полная оплата' : 'предзаказ'})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersModal({
  show,
  onClose,
  selectedCategory,
  setSelectedCategory,
  searchTerm,
  setSearchTerm,
  priceRange,
  setPriceRange,
  selectedFeatures,
  setSelectedFeatures,
  selectedColors,
  setSelectedColors,
  selectedMemory,
  setSelectedMemory,
  selectedSIM,
  setSelectedSIM,
  filterOptions,
  expandedSections,
  toggleSection,
  resetFilters,
  filteredPhones,
  phones,
  categories
}) {
  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content filters-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        <h2>Фильтры</h2>
        <p className="filters-subtitle">
          Выберите параметры для поиска товаров
          <span className="filters-count-info">
            (Найдено: {filteredPhones ? filteredPhones.length : phones.length} товаров)
          </span>
        </p>

        <div className="filters-container">
          {/* Category Filter */}
          <div className="filter-group">
            <div className="filter-header" onClick={() => toggleSection('category')}>
              <label>Категория:</label>
              <span className={`filter-toggle ${expandedSections.category ? 'expanded' : ''}`}>▼</span>
            </div>
            <div className={`filter-content ${expandedSections.category ? 'expanded' : ''}`}>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="all">Все товары</option>
                {categories && categories.filter(cat => cat.visible !== false).map(category => (
                  <option key={category.slug} value={category.slug}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Filter */}
          <div className="filter-group">
            <div className="filter-header" onClick={() => toggleSection('search')}>
              <label>Поиск:</label>
              <span className={`filter-toggle ${expandedSections.search ? 'expanded' : ''}`}>▼</span>
            </div>
            <div className={`filter-content ${expandedSections.search ? 'expanded' : ''}`}>
              <input
                type="text"
                placeholder="Название или описание..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="filter-group">
            <div className="filter-header" onClick={() => toggleSection('price')}>
              <label>Цена (₽):</label>
              <span className={`filter-toggle ${expandedSections.price ? 'expanded' : ''}`}>▼</span>
            </div>
            <div className={`filter-content ${expandedSections.price ? 'expanded' : ''}`}>
              <div className="price-range">
                <input
                  type="number"
                  placeholder="от"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
                  className="filter-input price-input"
                />
                <span>-</span>
                <input
                  type="number"
                  placeholder="до"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
                  className="filter-input price-input"
                />
              </div>
            </div>
          </div>

          {/* Dynamic Filters based on category */}
          {selectedCategory !== 'all' && (
            <>
              {/* Features Filter */}
              {filterOptions[selectedCategory].features.length > 0 && (
                <div className="filter-group">
                  <div className="filter-header" onClick={() => toggleSection('features')}>
                    <label>Особенности:</label>
                    <span className={`filter-toggle ${expandedSections.features ? 'expanded' : ''}`}>▼</span>
                  </div>
                  <div className={`filter-content ${expandedSections.features ? 'expanded' : ''}`}>
                    <div className="features-filter">
                      {filterOptions[selectedCategory].features.map(feature => (
                        <label key={feature} className="feature-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedFeatures.includes(feature)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFeatures([...selectedFeatures, feature]);
                              } else {
                                setSelectedFeatures(selectedFeatures.filter(f => f !== feature));
                              }
                            }}
                          />
                          <span>{feature}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Colors Filter */}
              {filterOptions[selectedCategory].colors.length > 0 && (
                <div className="filter-group">
                  <div className="filter-header" onClick={() => toggleSection('colors')}>
                    <label>Цвет:</label>
                    <span className={`filter-toggle ${expandedSections.colors ? 'expanded' : ''}`}>▼</span>
                  </div>
                  <div className={`filter-content ${expandedSections.colors ? 'expanded' : ''}`}>
                    <div className="features-filter">
                      {filterOptions[selectedCategory].colors.map(color => (
                        <label key={color} className="feature-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedColors.includes(color)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedColors([...selectedColors, color]);
                              } else {
                                setSelectedColors(selectedColors.filter(c => c !== color));
                              }
                            }}
                          />
                          <span>{color}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Memory Filter */}
              {filterOptions[selectedCategory].memory.length > 0 && (
                <div className="filter-group">
                  <div className="filter-header" onClick={() => toggleSection('memory')}>
                    <label>Память:</label>
                    <span className={`filter-toggle ${expandedSections.memory ? 'expanded' : ''}`}>▼</span>
                  </div>
                  <div className={`filter-content ${expandedSections.memory ? 'expanded' : ''}`}>
                    <div className="features-filter">
                      {filterOptions[selectedCategory].memory.map(memory => (
                        <label key={memory} className="feature-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedMemory.includes(memory)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMemory([...selectedMemory, memory]);
                              } else {
                                setSelectedMemory(selectedMemory.filter(m => m !== memory));
                              }
                            }}
                          />
                          <span>{memory}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* SIM Filter */}
              {filterOptions[selectedCategory].sim.length > 0 && (
                <div className="filter-group">
                  <div className="filter-header" onClick={() => toggleSection('sim')}>
                    <label>SIM:</label>
                    <span className={`filter-toggle ${expandedSections.sim ? 'expanded' : ''}`}>▼</span>
                  </div>
                  <div className={`filter-content ${expandedSections.sim ? 'expanded' : ''}`}>
                    <div className="features-filter">
                      {filterOptions[selectedCategory].sim.map(sim => (
                        <label key={sim} className="feature-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedSIM.includes(sim)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSIM([...selectedSIM, sim]);
                              } else {
                                setSelectedSIM(selectedSIM.filter(s => s !== sim));
                              }
                            }}
                          />
                          <span>{sim}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>

        <div className="modal-actions">
          {(selectedCategory !== 'all' || searchTerm || priceRange.min || priceRange.max ||
            selectedFeatures.length > 0 || selectedColors.length > 0 ||
            selectedMemory.length > 0 || selectedSIM.length > 0) && (
            <button onClick={resetFilters} className="reset-filters-btn">
              Сбросить фильтры
            </button>
          )}
          <button onClick={onClose} className="apply-filters-btn">
            Применить фильтры
          </button>
        </div>
      </div>
    </div>
  );
}

function UserProfile({ user }) {
  console.log('🎯 UserProfile rendering with user:', user);

  if (!user) {
    console.log('❌ UserProfile: No user data');
    return null;
  }

  const displayName = user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Пользователь';
  const avatarUrl = user.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=007aff&color=fff&size=40`;

  console.log('✅ UserProfile: Display name:', displayName);
  console.log('✅ UserProfile: Avatar URL:', avatarUrl);

  return (
    <div className="user-profile">
      <img
        src={avatarUrl}
        alt={displayName}
        className="user-avatar"
        onError={(e) => {
          console.log('❌ Avatar load error, using fallback');
          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=ff3b30&color=fff&size=40`;
        }}
      />
      <span className="user-name">@{user.username || displayName}</span>
    </div>
  );
}

export default App;
